import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  adminUserApi,
  type UserUpdateDTO,
  type UserUpdateStatusDTO,
  type AdminListDTO,
} from '../api/adminUserApi';
import { type AdminUser } from '@/types/user';
import { handleApiError } from '@/lib/api';

// Query keys for consistent cache management
export const adminUserManagementKeys = {
  all: ['adminUserManagement'] as const,
  lists: () => [...adminUserManagementKeys.all, 'list'] as const,
  list: (query?: string) =>
    [...adminUserManagementKeys.lists(), { query }] as const,
  details: () => [...adminUserManagementKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminUserManagementKeys.details(), id] as const,
  rolePermissions: () =>
    [...adminUserManagementKeys.all, 'rolePermissions'] as const,
};

// Hook to fetch users list with search
export function useAdminUsers(query?: string) {
  return useQuery({
    queryKey: adminUserManagementKeys.list(query),
    queryFn: () => adminUserApi.getUsers(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    // Retry logic is handled globally in QueryProvider
  });
}

// Hook to create a new user
export function useCreateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminUserApi.createUser,
    onSuccess: (data) => {
      // Invalidate user list to refetch with new user
      queryClient.invalidateQueries({
        queryKey: adminUserManagementKeys.lists(),
      });

      // Show success message
      toast.success('User created successfully', {
        description:
          data.sync_status === 'synced'
            ? 'The user has been added to the system.'
            : 'User creation is pending. Please check back later.',
      });
    },
    onError: (error) => {
      handleApiError(error, { context: 'create_user' });
      toast.error('Failed to create user', {
        description:
          error instanceof Error ? error.message : 'Please try again later.',
      });
    },
  });
}

// Hook to update user status
export function useUpdateAdminUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminUserApi.updateUserStatus,
    onMutate: async (data: UserUpdateStatusDTO) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: adminUserManagementKeys.lists(),
      });

      // Snapshot the previous values for all matching queries
      const previousQueries = queryClient.getQueriesData({
        queryKey: adminUserManagementKeys.lists(),
      });

      // Optimistically update the user status
      queryClient.setQueriesData(
        { queryKey: adminUserManagementKeys.lists() },
        (old: AdminListDTO | undefined) => {
          if (!old?.admins) return old;
          return {
            ...old,
            admins: old.admins.map((user: AdminUser) =>
              user.id === data.user_id
                ? { ...user, is_active: data.is_active }
                : user
            ),
          };
        }
      );

      // Return context with snapshot
      return { previousQueries };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        // Restore all queries to their previous state
        context.previousQueries.forEach(([queryKey, previousData]) => {
          queryClient.setQueryData(queryKey, previousData);
        });
      }

      handleApiError(error, { context: 'update_user_status' });
      toast.error('Failed to update user status', {
        description:
          error instanceof Error ? error.message : 'Please try again later.',
      });
    },
    onSuccess: (_, variables) => {
      toast.success(
        variables.is_active ? 'User activated' : 'User deactivated',
        {
          description: variables.is_active
            ? 'The user can now access the system.'
            : 'The user has been deactivated.',
        }
      );
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: adminUserManagementKeys.lists(),
      });
    },
  });
}

// Hook to update user details
export function useUpdateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: UserUpdateDTO;
    }) => adminUserApi.updateUser(userId, data),
    onSuccess: (_, variables) => {
      // Invalidate both the user detail and list
      queryClient.invalidateQueries({
        queryKey: adminUserManagementKeys.detail(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: adminUserManagementKeys.lists(),
      });

      toast.success('User updated successfully');
    },
    onError: (error) => {
      handleApiError(error, { context: 'update_user' });
      toast.error('Failed to update user', {
        description:
          error instanceof Error ? error.message : 'Please try again later.',
      });
    },
  });
}

// Hook to delete a user (when endpoint is available)
export function useDeleteAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminUserApi.deleteUser,
    onSuccess: () => {
      // Invalidate user list
      queryClient.invalidateQueries({
        queryKey: adminUserManagementKeys.lists(),
      });

      toast.success('User deleted successfully');
    },
    onError: (error) => {
      handleApiError(error, { context: 'delete_user' });
      toast.error('Failed to delete user', {
        description:
          error instanceof Error ? error.message : 'Please try again later.',
      });
    },
  });
}

// Hook to get role permissions
export function useRolePermissions() {
  return useQuery({
    queryKey: adminUserManagementKeys.rolePermissions(),
    queryFn: adminUserApi.getRolePermissions,
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}
